/**
 * Settings → Integrations → "Adapty · webhook URL per app".
 *
 * Adapty puts no app id in a webhook event, so events of several Adapty apps
 * on the one org URL can't be told apart. Each Adapty app gets its own URL
 * here; the event's store then picks the iOS or the Android row. The org URL
 * on the Adapty card keeps working the old way.
 */
import { useState } from "react";
import { Btn } from "@/components/Btn";
import { I } from "@/components/Icon";
import { useAdaptyWebhookApps, useCatalogueApps, useSaveAdaptyWebhookApp } from "@/lib/api-hooks";
import {
  formFor,
  linkableApps,
  sameForm,
  suggestedAndroidName,
  toPayload,
  urlStatus,
  urlStatusShort,
  urlStatusText,
  validateForm,
  type AdaptyAppForm,
  type UrlStatus,
} from "@/lib/adapty-webhook-apps";
import { describeApiError } from "@/lib/api-error";
import { useDiscardGuard } from "@/lib/discard-guard";
import type { AdaptyWebhookApp, AdaptyWebhookAppLink } from "@/lib/types";

export function AdaptyAppUrlsCard() {
  const list = useAdaptyWebhookApps(true);
  const [editing, setEditing] = useState<AdaptyWebhookApp | "new" | null>(null);
  const rows = list.data ?? [];

  return (
    <div className="card card-pad" style={{ marginTop: 14 }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 16, marginBottom: 6 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>Adapty · webhook URL per app</div>
          <div className="muted" style={{ fontSize: 12.5, marginTop: 4, lineHeight: 1.5, maxWidth: 760 }}>
            Adapty doesn&apos;t say which app an event belongs to, so give each Adapty app its own URL:
            paste it into that app&apos;s webhook settings in Adapty (production endpoint URL). Its events
            are then assigned to the app — the iOS or the Android one, by store.
          </div>
        </div>
        <Btn icon={I.plus} onClick={() => setEditing("new")}>Add app</Btn>
      </div>

      {list.isLoading && <div className="muted" style={{ fontSize: 12.5, marginTop: 12 }}>Loading…</div>}
      {list.isError && (
        <div style={{ fontSize: 12.5, marginTop: 12, color: "#c5221f" }}>
          {describeApiError(list.error, "Couldn't load the per-app URLs.")}
        </div>
      )}
      {!list.isLoading && !list.isError && rows.length === 0 && (
        <div className="muted" style={{ fontSize: 12.5, marginTop: 12 }}>
          No apps yet — add one to get its URL.
        </div>
      )}
      {rows.map((w) => (
        <AppUrlRow key={w.id} row={w} onEdit={() => setEditing(w)} />
      ))}

      {editing && (
        <AdaptyAppModal
          editing={editing === "new" ? null : editing}
          all={rows}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

const STATUS_STYLE: Record<UrlStatus["kind"], { dot: string; text: string }> = {
  receiving: { dot: "var(--c-pos-500)", text: "var(--c-pos-600)" },
  confirmed: { dot: "var(--c-accent-500)", text: "var(--c-text-2)" },
  waiting: { dot: "var(--c-text-4)", text: "var(--c-text-3)" },
};

function fullWhen(iso: string): string {
  return new Date(iso).toLocaleString();
}

function shortWhen(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
  });
}

function UrlStatusLine({ status, text }: { status: UrlStatus; text: string }) {
  const st = STATUS_STYLE[status.kind];
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12, color: st.text }}>
      <span
        style={{
          width: 7, height: 7, borderRadius: "50%", background: st.dot, flex: "0 0 auto",
          boxShadow: status.kind === "receiving" ? `0 0 0 3px ${st.dot}25` : "none",
        }}
      />
      {text}
    </span>
  );
}

/** For the Adapty tile: every app on its own URL with where it stands, so
 *  the tile says what is actually switched on. */
export function AdaptyAppsSummary() {
  const list = useAdaptyWebhookApps(true);
  const rows = list.data ?? [];
  if (list.isLoading || list.isError) return null;
  return (
    <div style={{ marginBottom: 12 }}>
      <div className="label" style={{ marginBottom: 6 }}>Apps with their own URL</div>
      {rows.length === 0 && (
        <div className="muted" style={{ fontSize: 12 }}>None yet — set them up in the card below.</div>
      )}
      {rows.map((w) => (
        <div
          key={w.id}
          style={{ display: "flex", alignItems: "center", gap: 10, padding: "4px 0", fontSize: 12.5 }}
        >
          <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {w.name}
          </span>
          <UrlStatusLine status={urlStatus(w)} text={urlStatusShort(urlStatus(w), shortWhen)} />
        </div>
      ))}
    </div>
  );
}

function LinkChip({ platform, link }: { platform: "iOS" | "Android"; link: AdaptyWebhookAppLink | null }) {
  return (
    <span className="badge" title={link ? link.bundle_id : undefined}>
      {platform}: {link ? link.name : "—"}
    </span>
  );
}

function AppUrlRow({ row, onEdit }: { row: AdaptyWebhookApp; onEdit: () => void }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    await navigator.clipboard.writeText(row.webhook_url);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div style={{ borderTop: "1px solid var(--c-border)", marginTop: 14, paddingTop: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, marginRight: 4 }}>{row.name}</div>
        <LinkChip platform="iOS" link={row.ios} />
        <LinkChip platform="Android" link={row.android} />
        <div style={{ flex: 1 }} />
        <Btn size="sm" icon={I.edit} onClick={onEdit}>Edit</Btn>
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <input className="input mono" value={row.webhook_url} readOnly style={{ flex: 1, fontSize: 12 }} />
        <Btn icon={I.copy} onClick={() => void onCopy()}>{copied ? "Copied!" : "Copy"}</Btn>
      </div>
      <div style={{ marginTop: 8 }}>
        <UrlStatusLine status={urlStatus(row)} text={urlStatusText(urlStatus(row), fullWhen)} />
      </div>
    </div>
  );
}

function AdaptyAppModal({
  editing,
  all,
  onClose: onCloseProp,
}: {
  editing: AdaptyWebhookApp | null;
  all: AdaptyWebhookApp[];
  onClose: () => void;
}) {
  const apps = useCatalogueApps();
  const save = useSaveAdaptyWebhookApp();
  const [initial] = useState<AdaptyAppForm>(() => formFor(editing));
  const [form, setForm] = useState<AdaptyAppForm>(initial);
  const [error, setError] = useState<string | null>(null);
  const guard = useDiscardGuard(true, !sameForm(form, initial), onCloseProp);
  const { ios, android } = linkableApps(apps.data ?? [], all, editing?.id ?? null);
  const problem = validateForm(form);

  const androidValue =
    form.android.kind === "existing" ? String(form.android.appId) : form.android.kind === "new" ? "new" : "";

  const onSave = async () => {
    setError(null);
    try {
      await save.mutateAsync({ id: editing?.id ?? null, body: toPayload(form) });
      onCloseProp();
    } catch (e) {
      setError(describeApiError(e, "Couldn't save."));
    }
  };

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 200, background: "rgba(14,18,32,0.45)",
        display: "grid", placeItems: "center", padding: 20,
      }}
      onClick={guard.requestClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(500px, 96vw)", background: "var(--c-surface)", borderRadius: 12,
          boxShadow: "var(--sh-3)", padding: 24,
        }}
      >
        <h3 style={{ marginTop: 0, marginBottom: 8 }}>{editing ? "Edit Adapty app" : "Add Adapty app"}</h3>
        <p className="muted" style={{ fontSize: 12.5, marginTop: 0, marginBottom: 18 }}>
          {editing
            ? "The URL stays the same. Events go to the apps linked below."
            : "The app gets its own webhook URL. Link the app’s iOS and Android versions — the event’s store picks one."}
        </p>

        <div className="field" style={{ marginBottom: 14 }}>
          <label className="label">Adapty app name</label>
          <input
            className="input"
            value={form.name}
            placeholder="As in Adapty, e.g. JPG to PDF"
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </div>

        <div className="field" style={{ marginBottom: 14 }}>
          <label className="label">iOS app</label>
          <select
            className="select"
            value={form.iosAppId ?? ""}
            onChange={(e) => {
              const id = e.target.value ? Number(e.target.value) : null;
              const picked = ios.find((a) => a.id === id);
              setForm({ ...form, iosAppId: id, name: form.name.trim() || picked?.name || "" });
            }}
          >
            <option value="">— none —</option>
            {ios.map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
        </div>

        <div className="field" style={{ marginBottom: 14 }}>
          <label className="label">Android app</label>
          <select
            className="select"
            value={androidValue}
            onChange={(e) => {
              const v = e.target.value;
              setForm({
                ...form,
                android:
                  v === "" ? { kind: "none" }
                  : v === "new" ? { kind: "new", name: suggestedAndroidName(form.name), bundleId: "" }
                  : { kind: "existing", appId: Number(v) },
              });
            }}
          >
            <option value="">— none —</option>
            {android.map((a) => (
              <option key={a.id} value={a.id}>{a.name} · {a.bundle_id}</option>
            ))}
            <option value="new">+ New Android app…</option>
          </select>
        </div>

        {form.android.kind === "new" && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
            <div className="field">
              <label className="label">Android app name</label>
              <input
                className="input"
                value={form.android.name}
                onChange={(e) =>
                  form.android.kind === "new" &&
                  setForm({ ...form, android: { ...form.android, name: e.target.value } })
                }
              />
            </div>
            <div className="field">
              <label className="label">Package name</label>
              <input
                className="input mono"
                value={form.android.bundleId}
                placeholder="com.company.app"
                onChange={(e) =>
                  form.android.kind === "new" &&
                  setForm({ ...form, android: { ...form.android, bundleId: e.target.value } })
                }
              />
            </div>
          </div>
        )}

        {(error || (problem && !sameForm(form, initial))) && (
          <div style={{ marginBottom: 12, fontSize: 12.5, color: error ? "#c5221f" : "var(--c-text-3)" }}>
            {error ?? problem}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Btn onClick={guard.requestClose}>Cancel</Btn>
          <Btn variant="primary" disabled={problem != null || save.isPending} onClick={() => void onSave()}>
            {save.isPending ? "Saving…" : editing ? "Save" : "Create URL"}
          </Btn>
        </div>
      </div>
    </div>
  );
}
